import React, {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { buildRemotegrantAgentApiJsonBody } from '../../api/httpClient/remotegrantAgentApiPayload';
import type BrandingData from '../../interfaces/BrandingData';
import { useAppStore } from '../../store/store';

// Interface describing the shape of the Branding Context
interface BrandingContextType {
  brandingData: BrandingData | null;
  getBrandingData: (withLoading?: boolean) => Promise<void>;
  isLoadingBrandingData: boolean;
}

// Create React Context with default values
export const BrandingContext = createContext<BrandingContextType>({
  brandingData: null,
  getBrandingData: async (_withLoading?) => {},
  isLoadingBrandingData: false,
});

interface BrandingProviderProps {
  children: ReactNode;
}

/** Image fields in BrandingData that may contain absolute storage URLs. */
const BRANDING_IMAGE_FIELDS: (keyof BrandingData)[] = [
  'emailNotificationLogo',
  'loginPageLogo',
  'topBarLogo',
  'quickShare',
];

/**
 * Transforms absolute storage URLs in branding data to use the backend asset proxy.
 * Storage URLs follow the pattern: https://.../enterprise-resources/{HASH}/images/{filename}
 * They are converted to: {API_BASE_URL}/assets/images/{filename}
 */
function transformBrandingImageUrls(data: BrandingData): BrandingData {
  const proxyBase = `${import.meta.env.VITE_APP_API_BASE_URL}/assets`;
  const hashPattern = /([A-Fa-f0-9]{64})\/(.*)/;
  const transformed = { ...data };

  for (const field of BRANDING_IMAGE_FIELDS) {
    const value = transformed[field];
    if (typeof value === 'string' && value.length > 0) {
      const match = value.match(hashPattern);
      if (match) {
        (transformed as Record<string, unknown>)[field] = `${proxyBase}/${match[2]}`;
        if (!transformed.hash) {
          transformed.hash = match[1];
        }
      }
    }
  }

  return transformed;
}

// BrandingProvider component providing context and branding data to its children
export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  // State for tracking loading, data, and errors
  const [isLoadingBrandingData, setIsLoadingBrandingData] = useState(true);
  const [brandingData, setBrandingData] = useState<BrandingData | null>(null);

  // Selectors and reducers from App Store
  const setBrandingDataStore = useAppStore((s) => s.setBrandingData);
  const localeFromStore = useAppStore((s) => s.locale);
  const setLocale = useAppStore((s) => s.setLocale);

  // i18n translation utilities
  const { i18n } = useTranslation();

  // Normalize a language string to lower case and base language, e.g. en-US -> en
  const normalizeLanguage = (language?: string) =>
    (language ?? '').toLowerCase().split('-')[0] || '';

  /**
   * Retrieve the supported i18n languages, filtering out special/internal values.
   * @returns An array of supported language codes.
   */
  const getSupportedLngs = () => {
    const list = Array.isArray(i18n.options?.supportedLngs)
      ? i18n.options.supportedLngs
      : undefined;
    // Remove internal i18next values such as "cimode" and "dev"
    return list?.filter((l) => l && l !== 'cimode' && l !== 'dev') as string[] | undefined;
  };

  /**
   * Selects the best matching language for the app, prioritizing:
   * 1. Preferred locale (from store)
   * 2. Browser languages
   * 3. Branding language (from branding.json)
   * 4. Fallback ('en')
   *
   * @param brandingLang - Language specified in branding.json
   * @param preferredLocale - Optional preferred locale (from app store)
   * @returns Best matching language code.
   */
  const pickBestLanguage = useCallback(
    (brandingLang?: string, preferredLocale?: string | null) => {
      const supported = getSupportedLngs();
      const supports = (lng?: string) => {
        const normalized = normalizeLanguage(lng);
        return !!normalized && (!supported || supported.includes(normalized));
      };

      // Prefer locale stored in app state if supported
      if (preferredLocale) {
        const normalizedPreferred = normalizeLanguage(preferredLocale);
        if (normalizedPreferred && supports(normalizedPreferred)) {
          return normalizedPreferred;
        }
      }

      // Collect possible languages from browser environment
      const browserCandidates: string[] = [];
      if (typeof navigator !== 'undefined') {
        if (Array.isArray(navigator.languages)) browserCandidates.push(...navigator.languages);
        if (navigator.language) browserCandidates.push(navigator.language);
      }

      // Normalize browser languages and deduplicate (e.g., ['it-IT', 'it'] -> ['it'])
      const normalizedBrowser = Array.from(
        new Set(browserCandidates.map(normalizeLanguage).filter(Boolean)),
      );

      // Use first matching browser language
      for (const cand of normalizedBrowser) {
        if (supports(cand)) return cand;
      }

      // Use branding-provided language
      const normalizedBranding = normalizeLanguage(brandingLang);
      if (normalizedBranding && supports(normalizedBranding)) return normalizedBranding;

      // Fallback: first supported, otherwise 'en'
      return (supported && supported[0]) || 'en';
    },
    [i18n],
  );

  /**
   * Change the app language (async), optionally loading resources if needed.
   * @param language - Language code to switch to.
   * @returns Promise that resolves to the language code.
   */
  const changeLanguage = useCallback(
    async (language: string) => {
      const nextLanguage = normalizeLanguage(language);
      const currentLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

      // Already in the desired language, or invalid
      if (!nextLanguage || nextLanguage === currentLanguage) {
        return i18n.language;
      }

      // Attempt to preload this language's resources (i18next extra feature)
      if (typeof i18n.loadLanguages === 'function') {
        try {
          await i18n.loadLanguages(nextLanguage);
        } catch {
          // Ignore resource load errors, fallback to regular change
        }
      }

      // Actually change the application's current language
      return i18n.changeLanguage(nextLanguage);
    },
    [i18n],
  );

  /**
   * Applies a language to the app and updates the store if locale not yet set.
   * @param lng - Language code to apply.
   * @returns Promise resolving when language is applied.
   */
  const applyLanguage = useCallback(
    async (lng: string) => {
      await changeLanguage(lng);
      if (!localeFromStore) {
        setLocale(lng);
      }
    },
    [changeLanguage, localeFromStore, setLocale],
  );

  /**
   * Loads branding configuration from the backend asset proxy endpoint.
   * The backend identifies the tenant via the Origin header (sent automatically by the browser).
   *
   * @param withLoading - Show loading spinner while fetching?
   */
  const getBrandingData = useCallback(
    async (withLoading = true) => {
      if (withLoading) {
        setIsLoadingBrandingData(true);
      }
      try {
        // Fetch branding.json via the backend proxy endpoint
        const apiBaseUrl = import.meta.env.VITE_APP_API_BASE_URL;
        const srToken = useAppStore.getState().authenticatedUser?.srToken;
        const agentPayloadBody = buildRemotegrantAgentApiJsonBody(
          apiBaseUrl,
          srToken ? { srToken } : {},
        );

        const res = await fetch(`${apiBaseUrl}/assets/branding.json`, {
          cache: 'no-store',
          ...(agentPayloadBody
            ? {
                headers: { 'Content-Type': 'application/json' },
                body: agentPayloadBody,
              }
            : {}),
        });
        if (res.ok) {
          // If fetch succeeds, transform storage URLs to proxy URLs, then update state and store
          const raw: BrandingData = await res.json();
          const data = transformBrandingImageUrls(raw);
          setBrandingData(data);
          setBrandingDataStore(data);
          // document.title: DocumentTitleSync reacts to branding store

          // Pick and apply best language (using branding or fallback)
          const bestLanguage = pickBestLanguage(data.language || 'en', localeFromStore);
          await applyLanguage(bestLanguage);
        } else {
          // If branding.json not found or fetch fails, apply best fallback language
          const bestLanguage = pickBestLanguage(undefined, localeFromStore);
          await applyLanguage(bestLanguage);
        }
      } catch (err) {
        // Log error and fallback: clear local branding, apply fallback language
        console.error('Error loading brandingData:', err);
        setBrandingData(null);
        const bestLanguage = pickBestLanguage(undefined, localeFromStore);
        await applyLanguage(bestLanguage);
      } finally {
        setIsLoadingBrandingData(false);
      }
    },
    [changeLanguage, pickBestLanguage, setBrandingDataStore, localeFromStore, applyLanguage],
  );

  // Memoize context value to prevent unnecessary rerenders for consumers
  const contextValue = useMemo(
    () => ({
      brandingData,
      getBrandingData,
      isLoadingBrandingData,
    }),
    [brandingData, getBrandingData, isLoadingBrandingData],
  );

  // Used to ensure branding is only initialized once (like componentDidMount)
  const initializedRef = useRef(false);

  // Load branding data on first mount
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    void getBrandingData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render children once branding data is loaded
  return (
    <BrandingContext.Provider value={contextValue}>
      {!isLoadingBrandingData && <>{children}</>}
    </BrandingContext.Provider>
  );
};
